/**
 * Builds a compact, compressed, URL-safe base64 payload of the user's portfolio
 * for migration to app.aktieutdelningar.com.
 *
 * Only reads the supplied portfolio array — never touches localStorage.
 *
 * @param {Array} portfolio - Redux portfolioReducer.portfolio
 * @returns {Promise<string>} URL-safe base64 encoded gzip payload
 */
export async function buildMigratePayload(portfolio) {
  const compact = {};
  for (const stock of portfolio) {
    compact[stock.api_id] = [stock.quantity, stock.gav ?? null];
  }

  const json = JSON.stringify(compact);

  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
  const buf = await new Response(stream).arrayBuffer();

  // Convert buffer to binary string safely (avoids stack overflow on large buffers)
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  // URL-safe base64 (RFC 4648 §5): replace + → -, / → _, strip padding =
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const LEGACY_TRANSFER_KIND = 'aktieutdelningar-legacy-localstorage-state';
export const MAIN_APP_MIGRATE_TARGET_URL = 'https://app.aktieutdelningar.com/';
export const LEGACY_TRANSFER_TARGET_URL = 'https://legacy.aktieutdelningar.com/';

/** Build the hardcoded holdings-import URL for the production main app. */
export function createMainAppMigrateUrl(payload) {
  const url = new URL(MAIN_APP_MIGRATE_TARGET_URL);
  url.hash = `import=${payload}`;
  return url.toString();
}

function bytesToBase64Url(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function stringToUtf8Bytes(value) {
  const binary = unescape(encodeURIComponent(value));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Build the full-state payload consumed by the new legacy domain. */
export function buildLegacyTransferPayload(state) {
  return bytesToBase64Url(
    stringToUtf8Bytes(
      JSON.stringify({
        kind: LEGACY_TRANSFER_KIND,
        version: 1,
        state,
      })
    )
  );
}

/** Build the hardcoded handoff URL for the replacement legacy origin. */
export function createLegacyTransferUrl(state) {
  const url = new URL(LEGACY_TRANSFER_TARGET_URL);
  url.hash = `legacy-import=${buildLegacyTransferPayload(state)}`;
  return url.toString();
}
